import { createContext, useContext, ReactNode, useEffect, useRef, useCallback, useState } from 'react';
import { useSession, getSession } from '../../lib/auth-client';
import { identifyUser, resetUser } from '../../lib/posthog';

interface AuthContextType {
  user: {
    id: string;
    email: string;
    name: string;
    image?: string | null;
    subscriptionTier?: string;
    emailVerified?: boolean;
  } | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  refreshSession: () => Promise<void>;
  updateUser: (userData: Partial<{ name: string; image: string | null }>) => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  isLoading: true,
  isAuthenticated: false,
  refreshSession: async () => {},
  updateUser: async () => {},
});

export function AuthProvider({ children }: { children: ReactNode }) {
  const { data: session, isPending, refetch } = useSession();
  const previousUserIdRef = useRef<string | null>(null);
  const [freshSession, setFreshSession] = useState<typeof session>(null);

  const refreshSession = useCallback(async () => {
    try {
      // Always use getSession with cache disabled to get fresh data
      const sessionData = await getSession({ query: { disableCookieCache: true } });
      
      // Update fresh session state to trigger re-render
      if (sessionData?.data) {
        setFreshSession(sessionData.data);
      }
      
      // Also try to refetch if available to update the useSession hook state
      if (refetch) {
        await refetch();
      }
    } catch (error) {
      console.error('Error refreshing session:', error);
    }
  }, [refetch]);

  const updateUser = useCallback(async (userData: Partial<{ name: string; image: string | null }>) => {
    try {
      const baseURL = import.meta.env.VITE_API_URL || 'http://localhost:4000';
      const response = await fetch(`${baseURL}/api/user/profile`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(userData),
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('Failed to update user profile');
      }

      // Refresh session to get updated user data
      await refreshSession();
    } catch (error) {
      console.error('Error updating user:', error);
      throw error;
    }
  }, [refreshSession]);

  const value: AuthContextType = {
    user: freshSession?.user ?? session?.user ?? null,
    isLoading: isPending,
    isAuthenticated: !!(freshSession?.user ?? session?.user),
    refreshSession,
    updateUser,
  };

  // freshSession is an optimistic overlay set by refreshSession(). When the
  // base session disappears (sign-out), it must be cleared too — otherwise the
  // stale overlay keeps the UI logged in after logout.
  useEffect(() => {
    if (!isPending && !session?.user) {
      setFreshSession(null);
    }
  }, [session, isPending]);

  // Track user identity changes for PostHog
  useEffect(() => {
    const currentUser = freshSession?.user ?? session?.user;
    const currentUserId = currentUser?.id ?? null;
    const previousUserId = previousUserIdRef.current;

    // User logged in
    if (currentUserId && currentUserId !== previousUserId) {
      identifyUser(currentUserId, {
        email: currentUser?.email,
        name: currentUser?.name,
      });
    }
    // User logged out
    else if (!currentUserId && previousUserId) {
      resetUser();
    }

    previousUserIdRef.current = currentUserId;
  }, [session, freshSession]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

